import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Phone, MessageCircle, Check, BadgeCheck, ShieldCheck, Timer, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Icon } from '@/components/icons';
import { useLang } from '@/context/LanguageContext';
import { useServices } from '@/context/ServicesContext';
import { ScrollReveal } from '@/components/common/Primitives';
import { RepairConsole } from '@/components/visuals/ITVisuals';
import { Tilt } from '@/components/experience/Experience';
import RepairRequestForm from '@/components/repair/RepairRequestForm';

const ACCENT = '#5AF3FF';
const SCRIM = 'linear-gradient(180deg, rgba(7,10,14,0.72) 0%, rgba(7,10,14,0.35) 45%, rgba(7,10,14,0.82) 100%)';

const CATEGORIES = [
  { key: 'pc-laptop', icon: 'Laptop', de: 'PC & Laptop', en: 'PC & Laptop' },
  { key: 'mac', icon: 'Apple', de: 'Mac & MacBook', en: 'Mac & MacBook' },
  { key: 'smartphone-tablet', icon: 'Smartphone', de: 'Smartphone & Tablet', en: 'Smartphone & Tablet' },
  { key: 'data-recovery', icon: 'HardDrive', de: 'Datenrettung', en: 'Data recovery' },
  { key: 'virus-removal', icon: 'Shield', de: 'Virenentfernung', en: 'Virus removal' },
  { key: 'hardware-upgrade', icon: 'Cpu', de: 'Hardware-Upgrade', en: 'Hardware upgrade' },
  { key: 'consoles', icon: 'Gamepad2', de: 'Spielkonsolen', en: 'Game consoles' },
  { key: 'printers-onsite', icon: 'Printer', de: 'Drucker & Vor-Ort', en: 'Printers & on-site' },
];

const GALLERY = [
  { src: 'https://images.unsplash.com/photo-1580894742597-87bc8789db3d?auto=format&fit=crop&w=900&q=80', de: 'Werkstatt · Diagnose', en: 'Workshop · diagnostics' },
  { src: 'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=900&q=80', de: 'Board-Level Reparatur', en: 'Board-level repair' },
  { src: 'https://images.unsplash.com/photo-1591799264318-7e6ef8ddb7ea?auto=format&fit=crop&w=900&q=80', de: 'Präzisions-Werkzeug', en: 'Precision tooling' },
  { src: 'https://images.unsplash.com/photo-1601737487795-dab272f52420?auto=format&fit=crop&w=900&q=80', de: 'Micro-Löttechnik', en: 'Micro soldering' },
];

const TRUST_ICONS = [Wallet, ShieldCheck, BadgeCheck, Timer];

function PhotoCard({ src, label, className = '', aspect = 'aspect-[4/3]' }) {
  const [broken, setBroken] = useState(false);
  return (
    <div className={`relative overflow-hidden rounded-2xl border border-[color:var(--itw-border)] bg-[color:var(--itw-surface)] neon-edge group ${aspect} ${className}`}
      style={{ boxShadow: '0 0 0 1px rgba(90,243,255,0.14), 0 0 24px rgba(0,231,255,0.08)' }}>
      {!broken ? (
        <img src={src} alt={label} loading="lazy" onError={() => setBroken(true)}
          className="absolute inset-0 h-full w-full object-cover object-center grayscale-[0.15] contrast-[1.05] transition-transform duration-500 group-hover:scale-105" />
      ) : (
        <div className="absolute inset-0" style={{ background: 'radial-gradient(120% 120% at 30% 20%, rgba(0,231,255,0.16), transparent 60%), linear-gradient(160deg,#0B1017,#0F1621)' }} />
      )}
      <div className="absolute inset-0" style={{ background: SCRIM }} />
      <div className="absolute inset-0 itw-grid opacity-20" />
      {label && <div className="absolute bottom-3 left-3 font-mono text-[11px] uppercase tracking-wider text-[color:var(--itw-text-2)]">{label}</div>}
    </div>
  );
}

export default function RepairPage() {
  const { t, tv, lang } = useLang();
  const { services } = useServices();
  const service = useMemo(() => services.find((s) => s.slug === 'geraete-reparatur'), [services]);
  const faq = service ? (tv(service.faq) || []) : [];

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const scrollToForm = () => {
    const el = document.getElementById('repair-form');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const badges = t('repair.badges');
  const trust = t('repair.trust');
  const process = t('repair.process');

  return (
    <div className="itw-noise" style={{ '--accent': ACCENT }}>
      {/* HERO */}
      <section data-testid="repair-hero-section" className="relative gradient-hero pt-28 lg:pt-36 pb-16 overflow-hidden">
        <div className="absolute inset-0 itw-grid opacity-40" />
        <div className="absolute top-1/4 right-[6%] h-[440px] w-[440px] rounded-full blur-3xl opacity-20 pointer-events-none" style={{ background: `radial-gradient(circle, ${ACCENT}, transparent 70%)` }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" data-testid="repair-back-link" className="inline-flex items-center gap-2 text-sm text-[color:var(--itw-text-2)] hover:text-[color:var(--accent)] transition-colors">
            <ArrowLeft className="h-4 w-4" /> {t('service.backToServices')}
          </Link>
          <div className="mt-8 grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.3em]" style={{ color: ACCENT }}>// {t('repair.kicker')}</div>
              <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
                className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[color:var(--itw-text)]">{t('repair.title')}</motion.h1>
              <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
                className="mt-4 text-base sm:text-lg text-[color:var(--itw-text-2)] leading-relaxed max-w-xl">{t('repair.subtitle')}</motion.p>
              <div className="mt-6 flex flex-wrap gap-2">
                {Object.values(badges).map((b, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium" style={{ color: ACCENT, background: `${ACCENT}12`, border: `1px solid ${ACCENT}33` }}>
                    <Check className="h-3.5 w-3.5" /> {b}
                  </span>
                ))}
              </div>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.35 }} className="mt-8 flex flex-wrap gap-3">
                <Button onClick={scrollToForm} data-testid="repair-hero-cta"
                  className="h-12 px-6 font-medium hover:opacity-90 transition-opacity rounded-full text-[color:var(--itw-bg)]"
                  style={{ backgroundColor: ACCENT, boxShadow: `0 0 28px ${ACCENT}55` }}>
                  {t('repair.ctaForm')} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <a href="tel:+498003603600" data-testid="repair-hero-call"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-[color:var(--itw-border)] text-[color:var(--itw-text)] hover:border-[color:var(--itw-border-strong)] transition-colors">
                  <Phone className="h-4 w-4" /> {t('repair.ctaCall')}
                </a>
              </motion.div>
            </div>
            <motion.div initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.7, delay: 0.3 }} className="space-y-4">
              <Tilt max={6}><RepairConsole accent={ACCENT} /></Tilt>
              <div className="grid grid-cols-2 gap-4">
                <PhotoCard src={GALLERY[0].src} label={GALLERY[0][lang] || GALLERY[0].de} aspect="aspect-[16/10]" />
                <PhotoCard src={GALLERY[1].src} label={GALLERY[1][lang] || GALLERY[1].de} aspect="aspect-[16/10]" />
              </div>
            </motion.div>
          </div>
        </div>
        <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-[color:var(--itw-bg)] to-transparent" />
      </section>

      {/* CATEGORIES */}
      <section data-testid="repair-categories-section" className="relative z-10 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[color:var(--itw-text)]">{t('repair.categoriesTitle')}</h2>
            <p className="mt-2 text-[color:var(--itw-text-2)]">{t('repair.categoriesSubtitle')}</p>
          </ScrollReveal>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CATEGORIES.map((c, i) => (
              <ScrollReveal key={c.key} delay={i * 0.05}>
                <button onClick={scrollToForm} data-testid={`repair-category-${c.key}`}
                  className="w-full text-left glass rounded-2xl p-5 h-full flex items-start gap-3 transition-[border-color,transform] duration-150 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(90,243,255,0.55)]"
                  style={{ borderColor: `${ACCENT}22` }}>
                  <span className="h-11 w-11 shrink-0 rounded-xl grid place-items-center" style={{ background: `${ACCENT}14`, border: `1px solid ${ACCENT}44` }}>
                    <Icon name={c.icon} className="h-5 w-5" style={{ color: ACCENT }} />
                  </span>
                  <div>
                    <div className="font-medium text-[color:var(--itw-text)]">{c[lang] || c.de}</div>
                    <div className="mt-1 inline-flex items-center gap-1 text-xs text-[color:var(--itw-text-3)] font-mono">{t('repair.ctaForm')} <ArrowRight className="h-3 w-3" /></div>
                  </div>
                </button>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS TIMELINE */}
      <section data-testid="repair-process-section" className="relative z-10 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[color:var(--itw-text)]">{t('repair.processTitle')}</h2>
            <p className="mt-2 text-[color:var(--itw-text-2)]">{t('repair.processSubtitle')}</p>
          </ScrollReveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-5 gap-4 relative">
            <div className="hidden md:block absolute top-6 left-[10%] right-[10%] h-px" style={{ background: `linear-gradient(90deg, transparent, ${ACCENT}55, transparent)` }} />
            {process.map((p, i) => (
              <ScrollReveal key={i} delay={i * 0.08}>
                <div className="relative glass rounded-2xl p-5 h-full" style={{ borderColor: `${ACCENT}22` }}>
                  <div className="h-12 w-12 rounded-full grid place-items-center font-mono text-sm font-semibold mb-3" style={{ background: `${ACCENT}14`, border: `1px solid ${ACCENT}55`, color: ACCENT }}>
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <h3 className="font-display font-semibold text-[color:var(--itw-text)]">{p.title}</h3>
                  <p className="mt-1.5 text-sm text-[color:var(--itw-text-2)] leading-relaxed">{p.text}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY STRIP */}
      <section className="relative z-10 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {GALLERY.map((g, i) => (
            <ScrollReveal key={i} delay={i * 0.06}><PhotoCard src={g.src} label={g[lang] || g.de} /></ScrollReveal>
          ))}
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="relative z-10 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal><h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[color:var(--itw-text)]">{t('repair.trustTitle')}</h2></ScrollReveal>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {trust.map((tr, i) => {
              const TI = TRUST_ICONS[i] || Check;
              return (
                <ScrollReveal key={i} delay={i * 0.06}>
                  <div className="glass rounded-2xl p-6 h-full" style={{ borderColor: `${ACCENT}22` }}>
                    <div className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: `${ACCENT}14`, border: `1px solid ${ACCENT}44` }}><TI className="h-5 w-5" style={{ color: ACCENT }} /></div>
                    <h3 className="mt-4 font-display font-semibold text-[color:var(--itw-text)]">{tr.title}</h3>
                    <p className="mt-1.5 text-sm text-[color:var(--itw-text-2)] leading-relaxed">{tr.text}</p>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* REQUEST FORM */}
      <section id="repair-form" data-testid="repair-form-section" className="relative z-10 py-14 scroll-mt-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-2xl">
              <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[color:var(--itw-text)]">{t('repair.form.title')}</h2>
              <p className="mt-2 text-[color:var(--itw-text-2)]">{t('repair.form.subtitle')}</p>
            </div>
          </ScrollReveal>
          <div className="mt-8"><RepairRequestForm /></div>
        </div>
      </section>

      {/* FAQ */}
      {faq.length > 0 && (
        <section className="relative z-10 py-14">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollReveal><h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[color:var(--itw-text)] mb-8">{t('service.faqTitle')}</h2></ScrollReveal>
            <Accordion type="single" collapsible className="space-y-3" data-testid="repair-faq">
              {faq.map((f, i) => (
                <AccordionItem key={i} value={`rfaq-${i}`} className="glass rounded-xl px-5" style={{ borderColor: `${ACCENT}22` }}>
                  <AccordionTrigger className="text-left text-[color:var(--itw-text)] hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-[color:var(--itw-text-2)] leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* BOTTOM CTA */}
      <section className="relative z-10 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="relative rounded-3xl overflow-hidden bg-[color:var(--itw-surface)] p-10 sm:p-14" style={{ border: `1px solid ${ACCENT}55`, boxShadow: `0 0 40px ${ACCENT}22` }}>
              <div className="absolute inset-0 itw-grid opacity-40" />
              <div className="relative flex flex-col lg:flex-row items-center justify-between gap-6">
                <div className="max-w-xl">
                  <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[color:var(--itw-text)]">{t('repair.title')}</h2>
                  <p className="mt-3 text-[color:var(--itw-text-2)]">{t('repair.subtitle')}</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button onClick={scrollToForm} data-testid="repair-bottom-cta"
                    className="h-12 px-8 font-medium hover:opacity-90 transition-opacity rounded-full whitespace-nowrap text-[color:var(--itw-bg)]"
                    style={{ backgroundColor: ACCENT, boxShadow: `0 0 28px ${ACCENT}55` }}>
                    {t('repair.ctaForm')} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <a href="https://wa.me/498003603600" target="_blank" rel="noreferrer" data-testid="repair-whatsapp"
                    className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10 transition-colors">
                    <MessageCircle className="h-4 w-4" /> WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
